import * as THREE from 'three'

const gridVertexShader = require('./shaders/grid/vertex.glsl')
const gridFragmentShader = require('./shaders/grid/fragment.glsl')

export default class Grid {
    /**
     * Constructor
     */
    constructor(_options) {
        this.time = _options.time
        this.sizes = _options.sizes
        this.cursor = _options.cursor
        this.renderer = _options.renderer
        this.hasTexture = _options.hasTexture

        this.name = 'grid'
        this.hasLevels = false

        if (this.hasTexture) {
            this.renderTarget = new THREE.WebGLRenderTarget(this.sizes.width, this.sizes.height, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat })
            this.texture = this.renderTarget.texture
        }

        this.setScene()
        this.setPlane()

        this.rayCaster = new THREE.Raycaster()
        this.cursorPosition = new THREE.Vector2()
    }

    /**
     * Set scene
     */
    setScene() {
        // Scene
        this.scene = new THREE.Scene()

        // Camera
        this.camera = new THREE.PerspectiveCamera(40, this.sizes.width / this.sizes.height, 0.1, 1000)
        this.camera.position.x = 1
        this.camera.position.y = 3
        this.camera.position.z = 5
        this.camera.rotation.order = 'YXZ'

        this.mouse = {}
        this.mouse.x = 0.5
        this.mouse.y = 0.5

        // // Dummy
        // this.dummy = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1, 0.1), new THREE.MeshBasicMaterial({ color: 0xff0000 }))
        // this.scene.add(this.dummy)
    }

    /**
     * Set plane
     */
    setPlane() {
        // Set up
        this.plane = {}
        this.plane.size = 12
        this.plane.subdivision = 12
        this.plane.canvasResolution = Math.pow(2, 12)

        // Canvas
        this.plane.$canvas = document.createElement('canvas')
        this.plane.$canvas.width = this.plane.canvasResolution
        this.plane.$canvas.height = this.plane.canvasResolution
        // this.plane.$canvas.style.position = 'absolute'
        // document.body.appendChild(this.plane.$canvas)

        // Context
        this.plane.context = this.plane.$canvas.getContext('2d')

        // Draw black background
        this.plane.context.fillRect(0, 0, this.plane.canvasResolution, this.plane.canvasResolution)

        // Draw lines on context
        this.plane.context.lineWidth = 1
        this.plane.context.strokeStyle = '#ffffff'
        const cellSize = this.plane.canvasResolution / this.plane.subdivision
        this.plane.context.beginPath()
        for (let i = 0; i < this.plane.subdivision + 1; i++) {
            this.plane.context.moveTo(Math.round(i * cellSize) - 0.5, 0)
            this.plane.context.lineTo(Math.round(i * cellSize) - 0.5, this.plane.canvasResolution)

            this.plane.context.moveTo(0, Math.round(i * cellSize) - 0.5)
            this.plane.context.lineTo(this.plane.canvasResolution, Math.round(i * cellSize) - 0.5)
        }
        this.plane.context.stroke()

        // Texture
        this.plane.texture = new THREE.Texture(this.plane.$canvas)
        this.plane.texture.needsUpdate = true

        const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy()
        this.plane.texture.anisotropy = maxAnisotropy

        // Geometry
        this.plane.geometry = new THREE.PlaneGeometry(this.plane.size, this.plane.size, this.plane.subdivision, this.plane.subdivision)

        // Material
        // this.plane.material = new THREE.MeshBasicMaterial({ map: this.plane.texture })
        this.plane.material = new THREE.ShaderMaterial({
            flatShading: false,
            transparent: true,
            uniforms:
            {
                uSubdivision: { type: 'f', value: this.plane.subdivision },
                uSize: { type: 'f', value: this.plane.size },
                uThickness: { type: 'f', value: 0.02 },
                uDotSize: { type: 'f', value: 0.0045 },
                uTexture: { type: 't', value: this.plane.texture },
                uTime: { type: 'f', value: this.time.elapsed },
                uCursorPosition: { type: 'f', value: new THREE.Vector3() }
            },
            vertexShader: gridVertexShader,
            fragmentShader: gridFragmentShader
        })

        // Mesh
        this.plane.mesh = new THREE.Mesh(this.plane.geometry, this.plane.material)
        this.plane.mesh.rotation.x = -Math.PI * 0.5
        this.scene.add(this.plane.mesh)
    }

    /**
     * Resize
     */
    resize() {
        // Camera
        this.camera.aspect = this.sizes.width / this.sizes.height
        this.camera.updateProjectionMatrix()
        
        // Render target
        if (this.hasTexture) {
            this.renderTarget.setSize(this.sizes.width, this.sizes.height)
        }
    }

    /**
     * Update
     */
    update() {
        this.plane.material.uniforms.uTime.value = this.time.elapsed

        // Mouse interaction
        this.mouse.x += (this.cursor.fromCenter.x - this.mouse.x) * 0.002 * this.time.delta
        this.mouse.y += (this.cursor.fromCenter.y - this.mouse.y) * 0.002 * this.time.delta

        this.cursorPosition.x = this.mouse.x * 2
        this.cursorPosition.y = -this.mouse.y * 2
        this.rayCaster.setFromCamera(this.cursorPosition, this.camera)

        const intersects = this.rayCaster.intersectObjects([this.plane.mesh], true)

        if (intersects.length > 0) {
            this.plane.material.uniforms.uCursorPosition.value = intersects[0].point
        }

        // this.dummy.position.x = this.plane.material.uniforms.uCursorPosition.value.x
        // this.dummy.position.y = this.plane.material.uniforms.uCursorPosition.value.y
        // this.dummy.position.z = this.plane.material.uniforms.uCursorPosition.value.z

        this.camera.position.x = 2
        this.camera.position.y = 2
        this.camera.position.z = 2
        this.camera.lookAt(this.scene.position)

        this.camera.rotation.y += this.mouse.x * 0.06
        this.camera.rotation.x += this.mouse.y * 0.08

        // Render
        this.render()
    }

    /**
     * Render
     */
    render() {
        if (this.hasTexture) {
            this.renderer.render(this.scene, this.camera, this.renderTarget)
        } else {
            this.renderer.render(this.scene, this.camera)
        }
    }

    /**
     * Dispose
     */
    dispose() {
        this.plane.texture.dispose()
        this.plane.geometry.dispose()

        if (this.hasTexture) {
            this.texture.dispose()
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/libs/experience/Grid.js